import React, { useEffect, useState, useContext } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { FiDownload, FiInfo } from "react-icons/fi";
import {
  Loader,
  CommonTable,
  DownloadExcelBill,
} from "../../Components/Common";
import { RESET_PAGINATION } from "../../Constants/Strings";
import * as attendanceService from "../../Services/attendanceService";
import {
  getUniqueDataById,
  formatDateDDMMYYYY,
  getFormattedDate,
} from "../../Utils";
import * as route from "../../routes";
import {
  MESSAGES,
  ATTENDANCE_COLUMNS,
  ATTENDANCE_LABELS,
  ADMIN_ATTENDANCE_TABLE_COLUMNS,
  ADMIN_ATTENDANCE_COLUMN_LABELS,
  APPROVED,
  REJECTED,
  PENDING_L1_APPROVAL,
  PENDING_L2_APPROVAL,
  EXCEL_FILE_COLUMNS,
  AUTO_APPROVED,
  CORRECTED,
  KPI_VIEW,
  KPI_VIEW_OPTIONS,
  KPI_USER_COLUMNS,
  KPI_USER_LABELS,
  getKpiCards,
} from "./constants";

import AttendanceForm from "./Attendance";
import MobileAttendanceCard from "./Component/MobileAttendanceCard";
import MobileKpiUserCard from "./Component/MobileKpiUserCard";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { getTimestampForFilename } from "../../Utils/timeUtils";
import { UserContext } from "../../Context";
import { USER_ROLES } from "../../Constants/Strings";
import SearchableDropdown from "../../Components/Common/SearchableDropdown";
import { getAllUsers } from "../../Services/UserService";
import { getSites } from "../../Services/siteService";
import { FiFilter } from "react-icons/fi";

// total + barClass add a progress bar (value out of total) with caption under it.
// segments ([{ value, barClass }]) instead split the bar into parts of `value`.
const KpiCard = ({
  label,
  value,
  icon: Icon,
  colorClass,
  total,
  barClass,
  caption,
  segments,
}) => {
  const percent = total ? Math.min(((value || 0) / total) * 100, 100) : 0;

  return (
    <div className="cursor-pointer border rounded-lg p-4 bg-white shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-full ${colorClass}`}>
          <Icon className="text-xl" />
        </div>
        <div>
          <div className="text-sm text-gray-500">{label}</div>
          <div className="text-2xl font-semibold text-gray-800">{value ?? "-"}</div>
        </div>
      </div>
      {(barClass || segments) && (
        <div className="mt-3">
          {segments ? (
            <div className="flex h-2 w-full gap-0.5 rounded-full overflow-hidden bg-gray-100">
              {segments.map((s) =>
                value && s.value ? (
                  <div
                    key={s.barClass}
                    className={`h-2 transition-all duration-500 ${s.barClass}`}
                    style={{ width: `${(s.value / value) * 100}%` }}
                  />
                ) : null,
              )}
            </div>
          ) : (
            <div className="h-2 w-full rounded-full bg-gray-100">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${barClass}`}
                style={{ width: `${percent}%` }}
              />
            </div>
          )}
          <div className="mt-1.5 text-xs text-gray-500">{caption}</div>
        </div>
      )}
    </div>
  );
};

const ListAttendance = () => {
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const canViewOtherUsersAttendance = [
    USER_ROLES.ADMIN,
    USER_ROLES.HR,
    USER_ROLES.DIRECTOR_MANAGEMENT,
    USER_ROLES.INCHARGE,
    USER_ROLES.PROJECT_INCHARGE,
  ].includes(user?.role);
  

  // Filters live in the route state so they survive opening a record's details
  // (the detail page navigates back with the same state) and a page reload
  // No date filter means the table shows all days; KPIs fall back to today
  const today = getFormattedDate(new Date());
  const [filters, setFilters] = useState({
    user: "",
    site: "",
    date: "",
    status: "",
    is_backdated:"",
    ...location.state?.filters,
  });
  const [kpi, setKpi] = useState(null);
  // Which list the toggle shows; ALL is the regular attendance records table
  const [kpiView, setKpiView] = useState(KPI_VIEW.ALL);
  const [kpiUsers, setKpiUsers] = useState([]);
  const [kpiUsersLoading, setKpiUsersLoading] = useState(false);

  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedUserObj, setSelectedUserObj] = useState(location.state?.selectedUser || null);
  const [selectedSiteObj, setSelectedSiteObj] = useState(location.state?.selectedSite || null);

  const applyFilters = (applied, selectedUser, selectedSite) => {
    setTempFilters(applied);
    setFilters(applied);
    setSelectedUserObj(selectedUser);
    setSelectedSiteObj(selectedSite);
    setShowFilter(false);
    navigate(".", {
      state: { filters: applied, selectedUser, selectedSite },
      replace: true,
    });
  };

  const fetchUsers = async (searchText) => {
    const search =
      typeof searchText === "string" ? searchText : searchText?.search || "";

    const res = await getAllUsers({ search });
    return res?.data?.results || [];
  };

  const fetchSites = async (searchText) => {
    const search =
      typeof searchText === "string" ? searchText : searchText?.search || "";

    const res = await getSites({ search });
    return res?.data?.results || [];
  };

  const getPaginationData = () => {
    if (pagination.offset > pagination.count) return;
    const finalParams = { ...pagination, ...filters };
    getAttendanceOverview(finalParams, false);
  };

  const getAttendanceOverview = async (finalParams, isInitial) => {
    try {
      const paramsWithFilters = {
        ...finalParams,
        ...filters,
      };

      const response =
        await attendanceService.getAttendanceList(paramsWithFilters);

      if (response.status === 200) {
        if (isInitial) {
          setAttendance(response.data.results);
        } else {
          setAttendance((prevData) =>
            getUniqueDataById(prevData, response.data.results),
          );
        }

        setPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: prev.offset + prev.limit,
        }));
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.REQUEST_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceKpi = async () => {
    try {
      const response = await attendanceService.getAttendanceKpi({
        date: filters.date || today,
        user: filters.user,
        site: filters.site,
      });
      if (response.status === 200) {
        setKpi(response.data);
      }
    } catch (error) {
      setKpi(null);
    }
  };

  const kpiCards = getKpiCards(kpi);

  useEffect(() => {
    setAttendance([]);
    setPagination(RESET_PAGINATION);
    setLoading(true);

    getAttendanceOverview({ ...RESET_PAGINATION }, true);
    if (canViewOtherUsersAttendance) getAttendanceKpi();
  }, [filters]);

  const getKpiUsers = async () => {
    setKpiUsersLoading(true);
    try {
      const response = await attendanceService.getAttendanceKpiUsers({
        type: kpiView,
        date: filters.date || today,
        user: filters.user,
        site: filters.site,
      });
      if (response.status === 200) {
        setKpiUsers(response.data.results || []);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.REQUEST_ERROR;
      toast.error(msg);
      setKpiUsers([]);
    } finally {
      setKpiUsersLoading(false);
    }
  };

  useEffect(() => {
    if (kpiView !== KPI_VIEW.ALL) getKpiUsers();
  }, [filters, kpiView]);

  const renderViewButton = (attendanceId) => (
    <button
      title="View"
      className="cursor-pointer text-primary hover:text-secondary mb-1"
      onClick={() =>
        navigate(route.VIEW_DETAILED_PAGE.replace(":id", attendanceId), {
          state: {
            ...location.state,
            from: location.pathname,
          },
        })
      }
    >
      <FiInfo className="text-lg" />
    </button>
  );

  const renderKpiUserField = (key, row) => {
    if (key === "actions") {
      // Pending users have no attendance record to open
      return row.attendance_id ? renderViewButton(row.attendance_id) : "-";
    }
    if (key === "status") {
      if (row.status === "CHECKED_OUT") {
        return <span className="text-purple-600 font-medium">Checked Out</span>;
      }
      if (row.status === "CHECKED_IN") {
        return <span className="text-green-600 font-medium">Checked In</span>;
      }
      return <span className="text-red-600 font-medium">Pending</span>;
    }
    if (["checkin_time", "checkout_time"].includes(key)) {
      return row[key] ? row[key].split(".")[0] : "-";
    }
    if (key === "site_name") {
      return row.site_name || "Non-Site";
    }
    if (key === "date") {
      return formatDateDDMMYYYY(row.date) || row.date;
    }
    return row[key];
  };

  const renderFields = (key, rowData) => {
    const value = rowData[key];

    if (key === "status") {
      switch (rowData.status) {
        case APPROVED:
          return <span className="text-green-600 font-medium">Approved</span>;
        case AUTO_APPROVED:
          return (
            <span className="text-green-600 font-medium">Auto Approved</span>
          );
        case REJECTED:
          return <span className="text-red-600 font-medium">Rejected</span>;
        case PENDING_L1_APPROVAL:
          return (
            <span className="text-grey-600 font-medium">
              Pending Approval
            </span>
          );
        case PENDING_L2_APPROVAL:
          return (
            <span className="text-grey-600 font-medium">
              Pending L2 Approval
            </span>
          );
        case CORRECTED:
          return <span className="text-blue-600 font-medium">Corrected</span>;
        default:
          return <span className="text-gray-500">Pending Approval</span>;
      }
    }

    if (
      ["checkin_time", "checkout_time"].includes(key) &&
      typeof value === "string"
    ) {
      return value.split(".")[0];
    }

    if (rowData.is_backdated === "Yes") {
      if (key === "checkin_photo_url" || key === "checkout_photo_url") {
        return "NA";
      }
    }

    switch (key) {
      case "is_backdated":
        return rowData.is_backdated === "Yes" ? (
          <span className="text-primary font-semibold">Yes</span>
        ) : <span className="text-grey-600 font-base">No</span>;
      case "status":
        return (
          <span>
            {rowData.status.charAt(0).toUpperCase() + rowData.status.slice(1)}
          </span>
        );
      case "actions":
        return renderViewButton(rowData.id);

      case "site_name":
        return rowData.site_name || "Non-Site";

      case "date":
        return formatDateDDMMYYYY(rowData.date) || rowData.date;

      default:
        return value;
    }
  };

  const downloadExcelData = () => {
    DownloadExcelBill({
      fetchFn: attendanceService.getAttendanceList,
      fieldMappings: EXCEL_FILE_COLUMNS,
      fileName: `Attendance_Data_${getTimestampForFilename()}.xlsx`,
    });
  };

  return (
    <div className="px-4 my-4">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="text-xl md:text-2xl text-primary font-semibold mb-3">
          Attendance View
        </div>

        <div className="flex items-center gap-3">
          <div className="flex justify-between items-center mb-4">
            <button
              className="cursor-pointer px-4 py-2.5 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
              onClick={() => {
                setTempFilters(filters);
                setShowFilter(true);
              }}
            >
              <FiFilter />
            </button>
          </div>

          <div className="flex justify-between items-center mb-4">
            <button
              className="cursor-pointer px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
              onClick={() => downloadExcelData()}
            >
              <FiDownload className="text-xl" />
            </button>
          </div>
        </div>
      </div>

      {/* KPI Section - follows the user/site/date filters */}
      {canViewOtherUsersAttendance && (
        <div className="mb-4">
          <div className="text-sm text-gray-500 mb-2">
            Showing for {formatDateDDMMYYYY(filters.date || today)}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {kpiCards.map((card) => (
              <KpiCard key={card.label} {...card} />
            ))}
          </div>
        </div>
      )}

      {/* Section for history */}
      {loading ? (
        <Loader />
      ) : isOpen ? (
        // Update the list after adding the attendance from child
        <AttendanceForm onClose={() => setIsOpen(false)} />
      ) : attendance ? (
        <>
          {showFilter && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
              <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4">
                {/* USER FILTER */}
                {(canViewOtherUsersAttendance) && (
                  <SearchableDropdown
                    label="User"
                    placeholder="Search user"
                    fetchFunction={fetchUsers}
                    labelKey="full_name"
                    valueKey="id"
                    includeAllOption
                    value={selectedUserObj}
                    onSelect={(user) => {
                      setSelectedUserObj(user);
                      setTempFilters((prev) => ({
                        ...prev,
                        user: user?.id || "",
                      }));
                    }}
                  />
                )}

                {/* STATUS FILTER */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={tempFilters.status || ""}
                    onChange={(e) =>
                      setTempFilters((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="cursor-pointer w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                  >
                    <option value="">All</option>
                    <option value={APPROVED}>Approved</option>
                    <option value={AUTO_APPROVED}>Auto Approved</option>
                    <option value={REJECTED}>Rejected</option>
                    <option value={PENDING_L1_APPROVAL}>
                      Pending Approval
                    </option>
                    <option value={CORRECTED}>Corrected</option>
                  </select>
                </div>

                {/* Backdated  */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Backdated
                    </label>

                    <select
                      value={tempFilters.is_backdated || ""}
                      onChange={(e) =>
                        setTempFilters((prev) => ({
                          ...prev,
                          is_backdated: e.target.value,
                        }))
                      }
                      className="cursor-pointer w-full min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-1 focus:ring-primary focus:outline-none"
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>

                {/* SITE FILTER */}
                <SearchableDropdown
                  label="Site"
                  placeholder="Search site"
                  fetchFunction={fetchSites}
                  labelKey="name"
                  valueKey="id"
                  includeAllOption
                  value={selectedSiteObj}
                  onSelect={(site) => {
                    setSelectedSiteObj(site);
                    setTempFilters((prev) => ({
                      ...prev,
                      site: site?.id || "",
                    }));
                  }}
                />

                {/* DATE FILTER */}
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={tempFilters.date || ""}
                    onChange={(e) =>
                      setTempFilters((prev) => ({
                        ...prev,
                        date: e.target.value,
                      }))
                    }
                    className="cursor-pointer w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                  />
                </div>

                {/* ACTION BUTTONS */}
                <div className="flex justify-between items-center pt-4">
                  <button
                    className="cursor-pointer px-4 py-2 rounded-lg bg-gray-200"
                    onClick={() => {
                      const cleared = { user: "", site: "", date: "", status: "", is_backdated: "",};
                      applyFilters(cleared, null, null);
                    }}
                  >
                    Clear
                  </button>

                  <div className="flex gap-2">
                    <button
                      className="cursor-pointer px-4 py-2 rounded-lg bg-gray-200"
                      onClick={() => {
                        setTempFilters(filters);
                        setSelectedUserObj(location.state?.selectedUser || null);
                        setSelectedSiteObj(location.state?.selectedSite || null);
                        setShowFilter(false);
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      className="cursor-pointer px-4 py-2 rounded-lg bg-primary text-white"
                      onClick={() =>
                        applyFilters(tempFilters, selectedUserObj, selectedSiteObj)
                      }
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Toggle between attendance records and the people behind the KPIs */}
          {canViewOtherUsersAttendance && (
            <div className="mb-3 overflow-x-auto">
              <div className="inline-flex gap-1 p-1 bg-gray-100 rounded-xl">
                {KPI_VIEW_OPTIONS.map((option) => {
                  const isActive = kpiView === option.value;
                  // All Users: total attendance records matching the filters (all days if no date)
                  const badgeCount =
                    option.value === KPI_VIEW.ALL
                      ? loading
                        ? null
                        : pagination.count
                      : kpi?.[option.countKey];
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setKpiView(option.value)}
                      className={`cursor-pointer flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                        isActive
                          ? "bg-white text-primary shadow"
                          : "text-gray-600 hover:text-gray-900 hover:bg-white/60"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${option.dotClass}`} />
                      {option.label}
                      <span
                        className={`min-w-[1.5rem] px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                          isActive
                            ? "bg-primary text-white"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {badgeCount ?? "-"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {kpiView !== KPI_VIEW.ALL ? (
            kpiUsersLoading ? (
              <Loader />
            ) : (
              <>
                {/* Desktop View */}
                <div className="hidden md:block h-[70vh] overflow-y-auto border rounded-md">
                  <CommonTable
                    tableColumns={KPI_USER_COLUMNS}
                    columnLabels={KPI_USER_LABELS}
                    tableRows={kpiUsers}
                    className={"cursor-pointer"}
                    customError={MESSAGES.NO_REQUESTS_AVAILABLE}
                    renderField={renderKpiUserField}
                  />
                </div>

                {/* Mobile View */}
                <div className="block md:hidden h-[70vh] overflow-y-auto">
                  {kpiUsers.length === 0 ? (
                    <div className="text-center text-gray-500 mt-10">
                      {MESSAGES.NO_REQUESTS_AVAILABLE}
                    </div>
                  ) : (
                    kpiUsers.map((item) => (
                      <MobileKpiUserCard key={item.user_id} data={item} />
                    ))
                  )}
                </div>
              </>
            )
          ) : (
          <>
          <div className="hidden md:block">
            <InfiniteScroll
              dataLength={attendance.length}
              next={getPaginationData}
              hasMore={pagination.offset <= pagination.count}
              scrollableTarget="scrollableDiv"
            >
              <div
                className="h-[70vh] overflow-y-auto md:border md:rounded-md"
                id="scrollableDiv"
              >
                <CommonTable
                  tableColumns={
                    (canViewOtherUsersAttendance ) ? ADMIN_ATTENDANCE_TABLE_COLUMNS : ATTENDANCE_COLUMNS
                  }
                  columnLabels={
                    (canViewOtherUsersAttendance) ? ADMIN_ATTENDANCE_COLUMN_LABELS : ATTENDANCE_LABELS
                  }
                  tableRows={attendance}
                  className={"cursor-pointer"}
                  customError={MESSAGES.NO_REQUESTS_AVAILABLE}
                  renderField={renderFields}
                />
              </div>
            </InfiniteScroll>
          </div>

          <div className="block md:hidden">
            <InfiniteScroll
              dataLength={attendance.length}
              next={getPaginationData}
              hasMore={pagination.offset <= pagination.count}
              scrollableTarget="mobileScrollableDiv"
            >
              <div
                className="h-[70vh] overflow-y-auto"
                id="mobileScrollableDiv"
              >
                {attendance.map((item) => (
                  <MobileAttendanceCard key={item.id} data={item} />
                ))}
              </div>
            </InfiniteScroll>
          </div>
          </>
          )}
        </>
      ) : (
        <div />
      )}
    </div>
  );
};

export default ListAttendance;
